import React, { ForwardedRef, ReactElement, useCallback } from 'react';

import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import Dropdown from '../Dropdown';
import { DropdownProps } from '../Dropdown/Dropdown';

export type DropdownControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the value is empty. */
  required?: boolean;
  /** Adds the `recommended` validator to the control. The control will display a warning if the value is empty. */
  recommended?: boolean;
};

export interface DropdownControlProps<Value, StateValue = Value, OptionValue = Value> extends
    Omit<DropdownProps<Value, OptionValue>, 'defaultValue' | 'name' | 'value' | 'children' |'onChange' | 'onBlur' | 'onKeyDown' | 'ref' | 'isValid' | 'isTouched'>,
    Omit<ControlProps<Value, StateValue>, 'children'>,
    DropdownControlValidationProps {
  ref?: ForwardedRef<HTMLInputElement>;
  /** Set the default value to the first option value if the control is required and has a single option. */
  defaultToUniqueOptionIfRequired?: boolean;
  /** @ignore */
  CustomDropdown?: (props: DropdownProps<Value, OptionValue>) => ReactElement<DropdownProps<Value, OptionValue>>;
}

interface DropdownControlInternalProps<Value> extends Omit<ControlRenderProps<Value, HTMLInputElement>, 'onTouched' | 'onValueChange' | 'id' | 'onBlur' | 'onFocus' | 'onChange' | 'onKeyDown'> {
  onTouchedCallback: ControlRenderProps<Value, HTMLInputElement>['onTouched'];
  onValueChangeCallback: ControlRenderProps<Value, HTMLInputElement>['onValueChange'];
}

export interface DropdownControlComposedProps<Value, StateValue, OptionValue> extends DropdownControlProps<Value, StateValue, OptionValue>, DropdownControlInternalProps<Value> {

}

export const DropdownControlDefaultProps: Partial<DropdownControlProps<any, any>> = {
  CustomDropdown: Dropdown,
  defaultToUniqueOptionIfRequired: true,
};

const DropdownControl = <Value, StateValue = Value, OptionValue = Value>({
  id, name, required, defaultValue, readOnly, textOnly, options, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, value, isUnionControlGroup, CustomDropdown, onValueChange, onTouched, onValueChangeCallback, onTouchedCallback, ...props
}: DropdownControlComposedProps<Value, StateValue, OptionValue>) => {
  const handleValueChange = useCallback((newValue: Value | OptionValue) => {
    onValueChangeCallback(newValue as Value);
    if (onValueChange) {
      onValueChange(newValue);
    }
  }, [onValueChange, onValueChangeCallback]);

  const handleTouched = useCallback((newValue: Value) => {
    onTouchedCallback(newValue);
    if (onTouched) {
      onTouched(newValue);
    }
  }, [onTouched, onTouchedCallback]);

  return (
    <CustomDropdown
      id={id}
      data-control
      data-testid="DropdownControl"
      aria-invalid={Boolean(!isValid && isTouched)}
      aria-errormessage={errorMessagesId}
      data-control-nesting="1"
      readOnly={readOnly}
      textOnly={textOnly}
      {...excludePropsByComponent(props, Control)}
      required={required}
      options={options}
      name={name}
      value={value}
      isValid={isValid}
      hasWarning={hasWarning}
      hasCaution={hasCaution}
      hasInfo={hasInfo}
      isTouched={isTouched}
      onValueChange={handleValueChange}
      onTouched={handleTouched}
      isUnionControlGroup={isUnionControlGroup}
    />
  );
};

DropdownControl.labeledControl = true;
DropdownControl.defaultProps = DropdownControlDefaultProps;

export default DropdownControl;
